import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Card } from '../components/ui/Card';
import { Input } from '../components/ui/Input';
import { Label } from '../components/ui/Label';
import { Button } from '../components/ui/Button';
import { Alert } from '../components/ui/Alert';
import { MockBadge } from '../components/MockBadge';
import { SELECT_CLASS, useSenders } from '../components/AudiencePicker';
import { apiFetch } from '../lib/api';
import { useAuth } from '../lib/auth';
import type { BrandKit } from '../types/api';

const HEX_RE = /^#[0-9a-fA-F]{6}$/;
const TRUSTPILOT_URL_RE = /^https:\/\/([a-z0-9-]+\.)*trustpilot\.com\/.+/i;
/** Same rule as the API (apps/app/src/routes/brandKit.ts): https and a dotted host, no spaces. */
const LEGAL_NOTICE_URL_RE = /^https:\/\/[a-z0-9-]+(\.[a-z0-9-]+)+(:\d+)?([/?#]\S*)?$/i;

export function BrandKitPage() {
  const { isAdmin } = useAuth();
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['brand-kit'],
    queryFn: () => apiFetch<BrandKit>('/brand-kit'),
  });
  const senders = useSenders();

  const [form, setForm] = useState<BrandKit | null>(null);

  useEffect(() => {
    if (data) setForm(data);
  }, [data]);

  const saveMutation = useMutation({
    mutationFn: (next: BrandKit) => apiFetch<BrandKit>('/brand-kit', { method: 'PUT', body: next }),
    onSuccess: (result) => {
      setForm(result);
      void queryClient.invalidateQueries({ queryKey: ['brand-kit'] });
    },
  });

  if (isLoading || !form) return <div className="text-muted">Loading brand kit…</div>;

  const colorsValid =
    HEX_RE.test(form.primaryColor) &&
    HEX_RE.test(form.textColor) &&
    HEX_RE.test(form.backgroundColor);
  const trustpilotUrlValid =
    !form.trustpilotReviewUrl || TRUSTPILOT_URL_RE.test(form.trustpilotReviewUrl);
  const legalNoticeUrlValid = !form.legalNoticeUrl || LEGAL_NOTICE_URL_RE.test(form.legalNoticeUrl);

  function update<K extends keyof BrandKit>(key: K, value: BrandKit[K]) {
    setForm((f) => (f ? { ...f, [key]: value } : f));
  }

  return (
    <>
      <header className="flex items-center gap-3">
        <h1 className="m-0 text-2xl font-semibold tracking-tight">Brand kit</h1>
        <MockBadge />
      </header>

      {!isAdmin ? (
        <Alert>You have read-only access. Contact an administrator to change the brand kit.</Alert>
      ) : null}
      {saveMutation.isSuccess ? (
        <div role="status" className="text-success-text text-sm">
          Saved.
        </div>
      ) : null}
      {saveMutation.isError ? <Alert>Could not save the changes. Try again.</Alert> : null}

      <div className="grid grid-cols-2 max-[860px]:grid-cols-1 gap-4 items-start">
        <div className="flex flex-col gap-3">
          <Card className="p-4 flex flex-col gap-3">
            <h2 className="text-sm font-semibold m-0">Logo &amp; colors</h2>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="bk-logo">Logo URL</Label>
              <Input
                id="bk-logo"
                value={form.logoUrl ?? ''}
                disabled={!isAdmin}
                onChange={(e) => update('logoUrl', e.target.value || null)}
                placeholder="https://…"
              />
            </div>
            <div className="grid grid-cols-3 max-sm:grid-cols-1 gap-2.5">
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="bk-primary">Primary color</Label>
                <Input
                  id="bk-primary"
                  value={form.primaryColor}
                  disabled={!isAdmin}
                  onChange={(e) => update('primaryColor', e.target.value)}
                  placeholder="#5457D6"
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="bk-text">Text color</Label>
                <Input
                  id="bk-text"
                  value={form.textColor}
                  disabled={!isAdmin}
                  onChange={(e) => update('textColor', e.target.value)}
                  placeholder="#18181B"
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="bk-bg">Background color</Label>
                <Input
                  id="bk-bg"
                  value={form.backgroundColor}
                  disabled={!isAdmin}
                  onChange={(e) => update('backgroundColor', e.target.value)}
                  placeholder="#F4F4F5"
                />
              </div>
            </div>
            {!colorsValid ? (
              <p className="m-0 text-xs text-danger-text">Colors must be written as #RRGGBB.</p>
            ) : null}
          </Card>

          <Card className="p-4 flex flex-col gap-3">
            <h2 className="text-sm font-semibold m-0">Footer</h2>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="bk-address">Sender details (fills {'{{ company_address }}'})</Label>
              <Input
                id="bk-address"
                aria-describedby="bk-address-help"
                value={form.companyAddress ?? ''}
                disabled={!isAdmin}
                onChange={(e) => update('companyAddress', e.target.value || null)}
                placeholder="Your brand name"
              />
              <p id="bk-address-help" className="m-0 text-xs text-muted">
                Who is sending, e.g. &apos;NoPain® Cream&apos;. A postal address is not required.
              </p>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="bk-legal-notice">
                Legal notice link (fills {'{{ legal_notice_url }}'}, optional)
              </Label>
              <Input
                id="bk-legal-notice"
                value={form.legalNoticeUrl ?? ''}
                disabled={!isAdmin}
                onChange={(e) => update('legalNoticeUrl', e.target.value || null)}
                placeholder="https://…"
              />
              {!legalNoticeUrlValid ? (
                <p className="m-0 text-xs text-danger-text">Must be an https:// link.</p>
              ) : null}
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="bk-footer">
                Footer HTML (sender details, unsubscribe link, etc.)
              </Label>
              <textarea
                id="bk-footer"
                className="w-full min-h-[110px] rounded-[9px] border border-zinc-300 px-3 py-2 text-sm font-mono disabled:bg-bg disabled:text-muted"
                value={form.footerHtml}
                disabled={!isAdmin}
                onChange={(e) => update('footerHtml', e.target.value)}
              />
            </div>
          </Card>

          <Card className="p-4 flex flex-col gap-3">
            <h2 className="text-sm font-semibold m-0">Sender &amp; tracking</h2>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="bk-sender">Default sender</Label>
              <select
                id="bk-sender"
                className={SELECT_CLASS}
                value={form.defaultSenderId ?? ''}
                disabled={!isAdmin || senders.isLoading}
                aria-describedby="bk-sender-hint"
                onChange={(e) => update('defaultSenderId', e.target.value || null)}
              >
                <option value="">The oldest sender</option>
                {form.defaultSenderId &&
                senders.data &&
                !senders.data.items.some((s) => s.id === form.defaultSenderId) ? (
                  <option value={form.defaultSenderId}>Archived sender</option>
                ) : null}
                {(senders.data?.items ?? []).map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.fromName} &lt;{s.fromEmail}&gt;
                  </option>
                ))}
              </select>
              <p id="bk-sender-hint" className="m-0 text-xs text-muted">
                Automations and signup confirmations are sent from this sender.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <input
                id="bk-utm"
                type="checkbox"
                checked={form.utm.enabled}
                disabled={!isAdmin}
                onChange={(e) => update('utm', { ...form.utm, enabled: e.target.checked })}
              />
              <Label htmlFor="bk-utm">Automatically add UTM parameters to links</Label>
            </div>
            {form.utm.enabled ? (
              <div className="grid grid-cols-2 gap-2.5">
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="bk-utm-source">utm_source</Label>
                  <Input
                    id="bk-utm-source"
                    value={form.utm.source}
                    disabled={!isAdmin}
                    onChange={(e) => update('utm', { ...form.utm, source: e.target.value })}
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="bk-utm-medium">utm_medium</Label>
                  <Input
                    id="bk-utm-medium"
                    value={form.utm.medium}
                    disabled={!isAdmin}
                    onChange={(e) => update('utm', { ...form.utm, medium: e.target.value })}
                  />
                </div>
              </div>
            ) : null}
          </Card>

          <Card className="p-4 flex flex-col gap-3">
            <h2 className="text-sm font-semibold m-0">Reviews</h2>
            <p className="text-xs text-muted m-0">
              Used by the review flow: every rating shows the same "Review us on Trustpilot" button
              (no filtering by rating). A rating of 3 or below, and any free-text feedback, is sent
              to the address below.
            </p>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="bk-trustpilot">Trustpilot review link</Label>
              <Input
                id="bk-trustpilot"
                value={form.trustpilotReviewUrl ?? ''}
                disabled={!isAdmin}
                onChange={(e) => update('trustpilotReviewUrl', e.target.value || null)}
                placeholder="https://dk.trustpilot.com/review/your-company.com"
              />
              {!trustpilotUrlValid ? (
                <p className="m-0 text-xs text-danger-text">
                  Must be an https://…trustpilot.com/ link.
                </p>
              ) : null}
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="bk-cs-email">Customer service email (internal messages)</Label>
              <Input
                id="bk-cs-email"
                type="email"
                value={form.reviewCustomerServiceEmail ?? ''}
                disabled={!isAdmin}
                onChange={(e) => update('reviewCustomerServiceEmail', e.target.value || null)}
                placeholder="support@your-company.com"
              />
            </div>
          </Card>

          {isAdmin ? (
            <Button
              disabled={
                !colorsValid ||
                !trustpilotUrlValid ||
                !legalNoticeUrlValid ||
                saveMutation.isPending
              }
              onClick={() => saveMutation.mutate(form)}
              className="self-end max-sm:self-stretch"
            >
              {saveMutation.isPending ? 'Saving…' : 'Save changes'}
            </Button>
          ) : null}
        </div>

        <Card className="p-0 overflow-hidden sticky top-4 max-[860px]:static">
          <div className="px-4 py-3 border-b border-border-soft text-sm font-semibold">Preview</div>
          <div
            style={{ backgroundColor: form.backgroundColor }}
            className="p-6 flex flex-col items-center gap-4"
          >
            {form.logoUrl ? (
              <img src={form.logoUrl} alt="Logo" className="h-10 object-contain" />
            ) : (
              <div className="h-10 w-24 rounded bg-black/10" aria-hidden="true" />
            )}
            <div
              className="w-full max-w-[320px] rounded-[14px] p-5 text-center"
              style={{ backgroundColor: '#FFFFFF', color: form.textColor }}
            >
              <p className="m-0 text-sm">
                This is roughly what an email looks like with your colors.
              </p>
              <button
                type="button"
                className="mt-3 inline-block rounded-[10px] px-4 py-2 text-sm font-medium text-white"
                style={{ backgroundColor: form.primaryColor }}
              >
                See the offer
              </button>
            </div>
          </div>
        </Card>
      </div>
    </>
  );
}
